import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layouts/AdminLayout';
import StatusBadge from '../../components/shared/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  FileText, 
  Truck, 
  AlertTriangle, 
  CheckCircle2,
  Clock,
  Package,
  ArrowRight,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentWaybills, setRecentWaybills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, waybillsRes] = await Promise.all([
          axios.get(`${API_URL}/api/dashboard/admin-stats`),
          axios.get(`${API_URL}/api/waybills?limit=10`)
        ]);

        setStats(statsRes.data);
        setRecentWaybills(waybillsRes.data.waybills);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="page-title">Operations Dashboard</h1>
          <p className="text-slate-600 mt-1">Monitor and manage all shipment operations</p>
        </div>

        {/* KPI Cards Row 1 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-elevated" data-testid="stat-quote-requests">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Quotes Requested</p>
                  <p className="text-3xl font-bold text-amber-600 mt-1">{stats?.quote_requests || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Awaiting pricing</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-amber-50 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-awaiting-approval">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Awaiting Approval</p>
                  <p className="text-3xl font-bold text-orange-600 mt-1">{stats?.awaiting_acceptance || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Sent to clients</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-orange-50 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-new-waybills">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">New Waybills</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">{stats?.new_waybills || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Ready for dispatch</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Package className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-in-transit">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">In Transit</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">{stats?.in_transit || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Active shipments</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Truck className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Second Row - Fleet Capacity and Alerts */}
        <div className="grid lg:grid-cols-1 gap-6">
          <Card className="card-elevated" data-testid="delayed-shipments">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="card-title">Delayed Shipments</CardTitle>
                    <p className="text-sm text-slate-500">{stats?.delayed || 0} shipments require attention</p>
                  </div>
                </div>
                <Link to="/admin/accepted-shipments?status=delayed">
                  <Button variant="outline" size="sm">View All</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {stats?.delayed_waybills?.length > 0 ? (
                <div className="space-y-3">
                  {stats.delayed_waybills.slice(0, 5).map((wb) => {
                    const daysOverdue = wb.delivery_expected_date 
                      ? Math.max(0, Math.floor((Date.now() - new Date(wb.delivery_expected_date).getTime()) / 86400000))
                      : null;
                    return (
                      <div key={wb.waybill_id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg" data-testid={`delayed-row-${wb.reference_number}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{wb.reference_number}</p>
                            {daysOverdue !== null && (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                {daysOverdue === 0 ? 'Due today' : `${daysOverdue}d overdue`}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">{wb.origin_city} &rarr; {wb.destination_city}</p>
                          {wb.delivery_expected_date && (
                            <p className="text-xs text-red-600 mt-0.5">Expected: {formatDate(wb.delivery_expected_date)}</p>
                          )}
                        </div>
                        <Link to={`/admin/shipment/${wb.waybill_id}`} state={{ from: '/admin/dashboard' }}>
                          <Button size="sm" className="bg-red-600 hover:bg-red-700">
                            Resolve
                          </Button>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                  <p>No delayed shipments</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Waybills Table */}
        <Card className="card-elevated" data-testid="recent-waybills">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Package className="h-5 w-5 text-slate-600" />
                </div>
                <CardTitle className="card-title">Recent Waybills</CardTitle>
              </div>
              <Link to="/admin/operations">
                <Button variant="outline" size="sm">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Waybill ID</th>
                    <th>Client</th>
                    <th>Route</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentWaybills.slice(0, 5).map((wb) => {
                    const detailLink = `/admin/shipment/${wb.waybill_id}`;
                    return (
                    <tr 
                      key={wb.waybill_id} 
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => navigate(detailLink, { state: { from: '/admin/dashboard' } })}
                    >
                      <td>
                        <span className="text-teal-700 font-medium">{wb.reference_number}</span>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600">
                            {wb.customer?.company_name?.substring(0, 2).toUpperCase() || 'CL'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{wb.customer?.company_name || wb.sender_company}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span>{wb.origin_city}</span>
                        <span className="text-slate-400 mx-2">→</span>
                        <span>{wb.destination_city}</span>
                      </td>
                      <td><StatusBadge status={wb.status} /></td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
